'use client'

import { ActionCard, type ActionCardTone } from './action-card'
import { Button } from './button'

/**
 * ResultCard — confirmation that something happened. Output card; the
 * richer cousin of the small "✓ Used X" chip. Use when the agent has
 * just done something the user should see — created a record, sent
 * an email, archived a doc — with optional follow-up actions (View,
 * Undo, Do another).
 *
 * Per `apps/agent/plans/plan-action-cards.md` Phase 4.
 */

export interface ResultCardProps {
  title: string
  description?: string
  attribution?: string
  state: 'ok' | 'error'
  /** One-line summary of what happened. */
  summary: string
  actions?: Array<{
    id: string
    label: string
    href?: string
    onClick?: () => void
    tone?: 'default' | 'destructive'
  }>
}

export function ResultCard({ title, description, attribution, state, summary, actions }: ResultCardProps) {
  const tone: ActionCardTone = state === 'ok' ? 'ok' : 'destructive'
  return (
    <ActionCard
      title={title}
      description={description}
      attribution={attribution}
      tone={tone}
      footer={
        actions && actions.length > 0 ? (
          <>
            {actions.map((a) => (
              <Button
                key={a.id}
                type="button"
                size="sm"
                variant={a.tone === 'destructive' ? 'destructive' : 'outline'}
                asChild={!!a.href}
                onClick={a.onClick}>
                {a.href ? <a href={a.href}>{a.label}</a> : <span>{a.label}</span>}
              </Button>
            ))}
          </>
        ) : null
      }>
      <div className="text-sm text-foreground">{summary}</div>
    </ActionCard>
  )
}
